import { useTranslation } from 'react-i18next';

import { Button } from '@mui/material';

import { sleep } from '@altirllc/shared/utils/functionalUtils';

import type { TModalsContext } from '@/contexts/ModalsProvider/types';

import { EMemberStatus } from '@/config/member';

import { FormikForm } from '@/components/formik/FormikForm';
import { FormikSubmit } from '@/components/formik/FormikSubmit';
import { GenericModal } from '@/components/modal/GenericModal';
import { GenericModalClose } from '@/components/modal/GenericModalClose';
import { GenericModalFooter } from '@/components/modal/GenericModalFooter';

import type { TMember, TInviteMemberSubmit } from '@/types/member';

import { inviteMemberSchema } from '@/validation/member';

import { styles } from '../MemberModal.styles';
import { InviteMemberFields } from './InviteMemberFields';

type TInviteMemberProps = TModalsContext & {
  onSuccess: (member: TMember) => void;
};

export const InviteMember = ({ close, onSuccess }: TInviteMemberProps) => {
  const { t } = useTranslation();

  const closeSelf = () => close('members.invite');

  const onSubmit: TInviteMemberSubmit = async (data, { setSubmitting }) => {
    const now = new Date().toISOString();

    await sleep(500);

    setSubmitting(false);
    onSuccess({
      ...data,
      userId: now,
      lastName: '',
      status: EMemberStatus.INVITED,
      statusChangedOn: now,
      lastActive: now,
    } as TMember);

    closeSelf();
  };

  return (
    <GenericModal sx={styles.modal}>
      <FormikForm validationSchema={inviteMemberSchema} onSubmit={onSubmit} sx={styles.body}>
        <GenericModalClose onClick={closeSelf} />
        <InviteMemberFields />

        <GenericModalFooter placeContent="end">
          <Button variant="outlined" onClick={closeSelf}>
            {t('common.cancel')}
          </Button>

          <FormikSubmit>{t('members.userForm.sendInvite')}</FormikSubmit>
        </GenericModalFooter>
      </FormikForm>
    </GenericModal>
  );
};
